import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, MapPin, Clock, Send } from "lucide-react";
import { api } from "../lib/api";
import { reportError } from "../lib/reportError";
import { useLanguage } from "../context/LanguageContext";

const IMG = "https://images.unsplash.com/photo-1567596388756-f6d710c8fc07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxtdXNpYyUyMHJlY29yZGluZyUyMHN0dWRpbyUyMG1pY3JvcGhvbmUlMjBoZWFkcGhvbmVzJTIwdmlueWwlMjBhbGJ1bSUyMHNvdW5kJTIwbWl4aW5nJTIwY29uc29sZXxlbnwwfHx8fDE3OTE1MTk0ODh8MA&ixlib=rb-4.1.0&q=85";

export const ContactDetails = () => {
  const { t } = useLanguage();
  return <div className="space-y-6 lg:col-span-2">
    <div className="studio-card fade-up overflow-hidden" style={{ animationDelay: "0.1s" }}><img src={IMG} alt={t("Condenser microphone in the Evi-Studio recording room")} className="h-44 w-full object-cover" /></div>
    <div className="studio-card fade-up space-y-5 p-6" style={{ animationDelay: "0.15s" }}>
      {[{ icon: Mail, label: "Email", text: "hello@evinet.group" }, { icon: MapPin, label: "Studio", text: "Evi-Studio · Evinet.group, Switzerland" }, { icon: Clock, label: "Hours", text: "Mon–Fri, 9:00–18:00 CET" }].map(item => <div key={item.label} className="flex gap-3">
        <item.icon size={18} className="mt-0.5 shrink-0 text-[#E5A93C]" />
        <div><p className="text-sm font-semibold text-slate-100">{t(item.label)}</p><p className="text-sm text-slate-400">{t(item.text)}</p></div>
      </div>)}
    </div>
  </div>;
};

export const ContactForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", topic: "General", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const inFlight = useRef(false);
  const set = name => event => setForm(current => ({ ...current, [name]: event.target.value }));
  const valid = form.name.trim() && /.+@.+\..+/.test(form.email) && form.message.trim();
  const submit = async event => {
    event.preventDefault();
    if (inFlight.current || !valid) return;
    inFlight.current = true;
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (error) {
      reportError("Contact message could not be sent", error);
      toast.error(t("Could not send your message. Please try again."));
    } finally { inFlight.current = false; setSending(false); }
  };
  if (sent) return <div data-testid="contact-success" className="py-10 text-center">
    <Send size={36} className="mx-auto text-[#10B981]" />
    <h2 className="mt-4 font-display text-xl font-bold text-slate-50">{t("Message received")}</h2>
    <p className="mt-2 text-sm text-slate-400">{t("Thank you, {{name}}. We will get back to you at {{email}} shortly.", { name: form.name, email: form.email })}</p>
  </div>;
  return <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
    <div className="grid gap-5 sm:grid-cols-2">
      <div><label className="studio-label" htmlFor="contact-name">{t("Name")}</label><input id="contact-name" data-testid="contact-name-input" className="studio-input" value={form.name} onChange={set("name")} placeholder={t("Your name")} /></div>
      <div><label className="studio-label" htmlFor="contact-email">{t("Email")}</label><input id="contact-email" type="email" data-testid="contact-email-input" className="studio-input" value={form.email} onChange={set("email")} placeholder="you@example.ch" /></div>
    </div>
    <div><label className="studio-label" htmlFor="contact-topic">{t("Topic")}</label>
      <select id="contact-topic" data-testid="contact-topic-select" className="studio-input" value={form.topic} onChange={set("topic")}>{["General", "Private Song", "Event Song", "Business / Jingle", "Album Production"].map(topic => <option key={topic} value={topic}>{t(topic)}</option>)}</select>
    </div>
    <div><label className="studio-label" htmlFor="contact-message">{t("Message")}</label><textarea id="contact-message" data-testid="contact-message-textarea" rows={6} className="studio-input" value={form.message} onChange={set("message")} placeholder={t("Tell us about your project…")} /></div>
    <button type="submit" data-testid="contact-submit-button" disabled={!valid || sending} className="btn-primary">{sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}{t("Send Message")}</button>
  </form>;
};

export default function Contact() {
  const { t } = useLanguage();
  return <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
    <p className="eyebrow">{t("Contact")}</p>
    <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl">{t("Talk to the studio")}</h1>
    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">{t("Questions about a package, a business jingle or an album project? Write to us — a human replies, not a bot.")}</p>
    <div className="mt-12 grid gap-8 lg:grid-cols-5"><div className="studio-card fade-up p-6 sm:p-8 lg:col-span-3"><ContactForm /></div><ContactDetails /></div>
  </div>;
}